import { useState, useRef, useEffect, useCallback, type RefObject } from 'react';

export const CONTEST_CARDS_PAGE_SIZE = 6;

export interface UsePaginatedScrollOptions {
  pageSize?: number;
}

/**
 * Find the nearest scrollable parent of an element
 */
function getScrollParent(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null;

  let parent = element.parentElement;
  while (parent) {
    const style = getComputedStyle(parent);
    const overflowY = style.overflowY;
    const isScrollable = overflowY === 'auto' || overflowY === 'scroll';

    if (isScrollable && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

/**
 * Check if an element is visible within its scroll container
 */
function isElementNearBottom(
  element: HTMLElement,
  scrollParent: HTMLElement | null,
  threshold: number = 100
): boolean {
  const rect = element.getBoundingClientRect();

  if (scrollParent) {
    const parentRect = scrollParent.getBoundingClientRect();
    // Check if element is within threshold of the scroll container's bottom
    return rect.top < parentRect.bottom + threshold;
  } else {
    // Fallback to window
    return rect.top < window.innerHeight + threshold;
  }
}

/**
 * Paginates a list for card views: show pageSize items initially,
 * then load pageSize more when the user scrolls to the bottom (sentinel).
 * Use sentinelRef on a div at the end of your list to trigger loading more.
 *
 * Shared hook for reuse across Pickems, Squares, and other games.
 *
 * Returns:
 * - visibleItems: the items to render
 * - sentinelRef: attach to a div at the end of your list
 * - hasMore: true if there are more items to load
 * - previousCount: the count before the last load (for fade-in animation)
 */
export function usePaginatedScroll<T>(
  items: T[],
  options: UsePaginatedScrollOptions = {}
): {
  visibleItems: T[];
  sentinelRef: RefObject<HTMLDivElement | null>;
  hasMore: boolean;
  previousCount: number;
} {
  const { pageSize = CONTEST_CARDS_PAGE_SIZE } = options;
  const [visibleCount, setVisibleCount] = useState(() => Math.min(pageSize, items.length));
  const [previousCount, setPreviousCount] = useState(0);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Track scroll parent
  const scrollParentRef = useRef<HTMLElement | null>(null);

  // Delay enabling scroll detection to skip initial render
  const [isReady, setIsReady] = useState(false);

  // Debounce loading to prevent multiple rapid loads
  const isLoadingRef = useRef(false);

  // Enable after a short delay
  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Cap visible count when list shrinks
  useEffect(() => {
    setVisibleCount(prev => Math.min(prev, items.length));
  }, [items.length]);

  // When list grows from empty, show first page
  useEffect(() => {
    if (items.length > 0 && visibleCount === 0) {
      setVisibleCount(Math.min(pageSize, items.length));
    }
  }, [items.length, pageSize, visibleCount]);

  const loadMore = useCallback(() => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;

    setPreviousCount(visibleCount);
    setVisibleCount(prev => Math.min(prev + pageSize, items.length));

    // Reset loading flag after a short delay
    setTimeout(() => {
      isLoadingRef.current = false;
    }, 100);
  }, [visibleCount, pageSize, items.length]);

  // Scroll-based detection that works with any scroll container
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || visibleCount >= items.length || !isReady) return;

    // Find and cache the scroll parent
    scrollParentRef.current = getScrollParent(sentinel);
    const scrollTarget = scrollParentRef.current || window;

    const handleScroll = () => {
      if (!sentinelRef.current || isLoadingRef.current) return;

      if (isElementNearBottom(sentinelRef.current, scrollParentRef.current, 150)) {
        loadMore();
      }
    };

    // Throttle scroll handler
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    scrollTarget.addEventListener('scroll', throttledScroll, { passive: true });

    // Also listen on window in case scroll bubbles up
    if (scrollTarget !== window) {
      window.addEventListener('scroll', throttledScroll, { passive: true });
    }

    return () => {
      scrollTarget.removeEventListener('scroll', throttledScroll);
      if (scrollTarget !== window) {
        window.removeEventListener('scroll', throttledScroll);
      }
    };
  }, [items.length, visibleCount, loadMore, isReady]);

  const visibleItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  return { visibleItems, sentinelRef, hasMore, previousCount };
}
